import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { projectsApi } from 'src/Services/API';
import { ProjectDto } from 'src/Services/API/generated';

export const useUnlikeProject = (options?: UseMutationOptions<void, unknown, string>) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<void, unknown, string, { previousProjects: ProjectDto[] | undefined }>(
    'unlike-project',
    async (projectId: string) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }
      await projectsApi.projectsControllerUnlike(projectId, assetId);
    },
    {
      ...options,
      async onMutate(projectId) {
        await queryClient.cancelQueries(['projects', assetId]);

        const previousProjects = queryClient.getQueryData<ProjectDto[]>(['projects', assetId]);

        if (previousProjects) {
          queryClient.setQueryData<ProjectDto[]>(
            ['projects', assetId],
            previousProjects.map(project => {
              if (project.id !== projectId) return project;

              const likesSummary = project.likesSummary || {
                count: 0,
                didILike: false,
                firstFive: [],
              };

              return {
                ...project,
                likesSummary: {
                  ...likesSummary,
                  count: Math.max(likesSummary.count - 1, 0),
                  didILike: false,
                },
              };
            })
          );
        }

        return { previousProjects };
      },
      onError(_err, _newTodo, context: { previousProjects: ProjectDto[] | undefined } | undefined) {
        if (context?.previousProjects) {
          queryClient.setQueryData(['projects', assetId], context.previousProjects);
        }
      },
      onSettled() {
        queryClient.invalidateQueries(['projects', assetId]);
      },
    }
  );
};
